import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Calendar,
  ChevronLeft,
  Save,
  Plus,
  Edit2,
  Check,
  X,
  AlertCircle,
  Clock,
  CalendarDays,
  Settings,
  Archive,
  CheckCircle2,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { useSeries } from '../../App';
import { useApi } from '../../contexts/ApiContext';

const parseLocalDate = (dateString) => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
  return new Date(year, month - 1, day);
};

const formatDate = (dateString, options = {}) => {
  const date = parseLocalDate(dateString);
  if (!date) return 'TBD';
  return date.toLocaleDateString('en-US', options);
};

function SeasonCard({ season, isSelected, onSelect, onEdit }) {
  const progress = season.events_count > 0
    ? Math.round((season.completed_events / season.events_count) * 100)
    : 0;

  return (
    <div
      className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
        isSelected
          ? 'border-rms-accent bg-rms-accent/5'
          : 'border-slate-200 hover:border-slate-300 bg-white'
      }`}
      onClick={() => onSelect(season)}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            season.is_active ? 'bg-emerald-100' : 'bg-slate-100'
          }`}>
            <Calendar className={`w-5 h-5 ${
              season.is_active ? 'text-emerald-600' : 'text-slate-500'
            }`} />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800">{season.season_name}</h3>
            <p className="text-sm text-slate-500">{season.season_year}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {season.is_active && (
            <Badge variant="success" dot>Active</Badge>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(season);
            }}
          >
            <Edit2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-500">Date Range</span>
          <span className="text-slate-700">
            {formatDate(season.start_date, { month: 'short', day: 'numeric' })} - {formatDate(season.end_date, { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-500">Events</span>
          <span className="text-slate-700">{season.completed_events} / {season.events_count} completed</span>
        </div>
        {season.events_count > 0 && (
          <div className="pt-2">
            <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  progress === 100 ? 'bg-emerald-500' : 'bg-rms-accent'
                }`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SeasonEditModal({ season, onSave, onClose }) {
  const [formData, setFormData] = useState({
    season_name: season?.season_name || '',
    season_year: season?.season_year || new Date().getFullYear(),
    start_date: season?.start_date || '',
    end_date: season?.end_date || '',
    is_active: season?.is_active || false
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 500));

    onSave({
      ...season,
      ...formData,
      season_id: season?.season_id || Date.now()
    });
    setSaving(false);
  };

  const isNew = !season?.season_id;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">
            {isNew ? 'Create New Season' : 'Edit Season'}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <Input
            label="Season Name"
            value={formData.season_name}
            onChange={(e) => setFormData({ ...formData, season_name: e.target.value })}
            placeholder="e.g., 2025 Season"
            required
          />

          <Input
            label="Season Year"
            type="number"
            value={formData.season_year}
            onChange={(e) => setFormData({ ...formData, season_year: parseInt(e.target.value) })}
            min={2020}
            max={2030}
            required
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Start Date"
              type="date"
              value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              required
            />
            <Input
              label="End Date"
              type="date"
              value={formData.end_date}
              onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              required
            />
          </div>

          <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4 rounded border-slate-300 text-rms-accent focus:ring-rms-accent"
            />
            <label htmlFor="is_active" className="text-sm text-slate-700">
              Set as active season
            </label>
          </div>

          {formData.is_active && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700">
                Setting this as the active season will deactivate any currently active season.
              </p>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isNew ? 'Create Season' : 'Save Changes'}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function SeasonConfig() {
  const { seriesSlug } = useParams();
  const currentSeries = useSeries();
  const api = useApi();

  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [editingSeason, setEditingSeason] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { isLoading: loading } = useQuery({
    queryKey: ['seasons', currentSeries?.dbId],
    queryFn: async () => {
      const result = await api.series.getSeasons(currentSeries.dbId);
      if (!result.success || !result.data?.seasons) throw new Error(result.message || 'Failed to load seasons');
      setSeasons(result.data.seasons);
      const activeSeason = result.data.seasons.find(s => s.is_active);
      setSelectedSeason(activeSeason || result.data.seasons[0] || null);
      return result.data.seasons;
    },
    enabled: !!currentSeries?.dbId,
  });

  const handleSaveSeason = async (savedSeason) => {
    const seriesId = currentSeries?.dbId;
    if (!seriesId) return;

    try {
      if (editingSeason) {
        // Update existing season
        const result = await api.series.updateSeason(seriesId, savedSeason.season_id, {
          season_year: savedSeason.season_year,
          start_date: savedSeason.start_date,
          end_date: savedSeason.end_date
        });
        if (result.success) {
          setSeasons(seasons.map(s =>
            s.season_id === savedSeason.season_id ? savedSeason :
            (savedSeason.is_active && s.is_active ? { ...s, is_active: false } : s)
          ));
          if (selectedSeason?.season_id === savedSeason.season_id) {
            setSelectedSeason(savedSeason);
          }
        }
      } else {
        // Create new season
        const result = await api.series.createSeason(seriesId, {
          season_year: savedSeason.season_year,
          start_date: savedSeason.start_date,
          end_date: savedSeason.end_date
        });
        if (result.success) {
          const newSeason = {
            ...savedSeason,
            season_id: result.data.season_id
          };
          const newSeasons = savedSeason.is_active
            ? seasons.map(s => ({ ...s, is_active: false }))
            : [...seasons];
          setSeasons([newSeason, ...newSeasons]);
          setSelectedSeason(newSeason);
        }
      }
    } catch (err) {
      console.error('Error saving season:', err);
    }
    setEditingSeason(null);
    setShowCreateModal(false);
  };

  const getDaysRemaining = (endDate) => {
    const end = parseLocalDate(endDate);
    if (!end) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  return (
    <div className="animate-fade-in">
      {/* Page Header */}
      <div className="mb-6">
        <Link
          to={`/${seriesSlug}/season`}
          className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-4"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Season Configuration</h1>
            <p className="text-slate-500 mt-1">
              Manage seasons for {currentSeries?.name || 'series'}
            </p>
          </div>
          <Button leftIcon={Plus} onClick={() => setShowCreateModal(true)}>
            New Season
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Season List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Seasons</CardTitle>
              <CardDescription>Select a season to view details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : seasons.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No seasons configured</p>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="mt-3"
                    onClick={() => setShowCreateModal(true)}
                  >
                    Create First Season
                  </Button>
                </div>
              ) : (
                seasons.map((season) => (
                  <SeasonCard
                    key={season.season_id}
                    season={season}
                    isSelected={selectedSeason?.season_id === season.season_id}
                    onSelect={setSelectedSeason}
                    onEdit={setEditingSeason}
                  />
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Season Details */}
        <div className="lg:col-span-2 space-y-6">
          {selectedSeason ? (
            <>
              {/* Current Season Overview */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>{selectedSeason.season_name}</CardTitle>
                      <CardDescription>Season overview and status</CardDescription>
                    </div>
                    {selectedSeason.is_active && (
                      <Badge variant="success" size="lg" dot>Active Season</Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                      <div className="flex items-center gap-2 mb-2">
                        <CalendarDays className="w-4 h-4 text-rms-accent" />
                        <span className="text-sm text-slate-500">Start Date</span>
                      </div>
                      <p className="font-semibold text-slate-800">
                        {formatDate(selectedSeason.start_date, { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                      <div className="flex items-center gap-2 mb-2">
                        <CalendarDays className="w-4 h-4 text-rms-accent" />
                        <span className="text-sm text-slate-500">End Date</span>
                      </div>
                      <p className="font-semibold text-slate-800">
                        {formatDate(selectedSeason.end_date, { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-4 h-4 text-rms-accent" />
                        <span className="text-sm text-slate-500">Days Remaining</span>
                      </div>
                      <p className="font-semibold text-slate-800">
                        {getDaysRemaining(selectedSeason.end_date) > 0
                          ? `${getDaysRemaining(selectedSeason.end_date)} days`
                          : 'Completed'
                        }
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        <span className="text-sm text-slate-500">Progress</span>
                      </div>
                      <p className="font-semibold text-slate-800">
                        {selectedSeason.events_count > 0
                          ? `${Math.round((selectedSeason.completed_events / selectedSeason.events_count) * 100)}%`
                          : '0%'
                        }
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Series-Specific Settings */}
              <Card>
                <CardHeader>
                  <CardTitle>Series Settings</CardTitle>
                  <CardDescription>Configuration specific to {currentSeries?.name || 'this series'}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 border border-slate-200">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-rms-accent/10 flex items-center justify-center">
                          <Settings className="w-5 h-5 text-rms-accent" />
                        </div>
                        <div>
                          <h4 className="font-medium text-slate-800">Points Configuration</h4>
                          <p className="text-sm text-slate-500">Configure point structures and tiebreakers</p>
                        </div>
                      </div>
                      <Link to={`/${seriesSlug}/config/points`}>
                        <Button variant="secondary" size="sm">Configure</Button>
                      </Link>
                    </div>

                    <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 border border-slate-200">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                          <Calendar className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <h4 className="font-medium text-slate-800">Events Schedule</h4>
                          <p className="text-sm text-slate-500">{selectedSeason.events_count} events scheduled</p>
                        </div>
                      </div>
                      <Link to={`/${seriesSlug}/events`}>
                        <Button variant="secondary" size="sm">View Events</Button>
                      </Link>
                    </div>

                    <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 border border-slate-200">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                          <Archive className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <h4 className="font-medium text-slate-800">Preload Classes</h4>
                          <p className="text-sm text-slate-500">Define vehicle classes for this season</p>
                        </div>
                      </div>
                      <Link to={`/${seriesSlug}/config/classes`}>
                        <Button variant="secondary" size="sm">Configure</Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle>Season Actions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      variant="secondary"
                      leftIcon={Edit2}
                      onClick={() => setEditingSeason(selectedSeason)}
                    >
                      Edit Season
                    </Button>
                    {!selectedSeason.is_active && (
                      <Button
                        variant="secondary"
                        leftIcon={Check}
                        onClick={() => {
                          const updated = { ...selectedSeason, is_active: true };
                          handleSaveSeason(updated);
                        }}
                      >
                        Set as Active
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      leftIcon={Archive}
                      onClick={() => {
                        if (window.confirm('Are you sure you want to archive this season?')) {
                          // Archive logic here
                        }
                      }}
                    >
                      Archive Season
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-800 mb-2">No Season Selected</h3>
                <p className="text-slate-500">
                  Select a season from the list or create a new one
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Edit/Create Modal */}
      {(editingSeason || showCreateModal) && (
        <SeasonEditModal
          season={editingSeason}
          onSave={handleSaveSeason}
          onClose={() => {
            setEditingSeason(null);
            setShowCreateModal(false);
          }}
        />
      )}
    </div>
  );
}
